import useGetOrCreateMidTermAdjustment from '@acorn-portal/shared/src//models/midTermAdjustment-model';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useManageAdditionalDriverDetails } from '@acorn-portal/shared/src/hooks/use-manage-additional-driver-details';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';
import { MtaChangeType } from '@acorn-portal/shared/src/services/api';
import { updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { omitFromArray } from '@acorn-portal/shared/src/utils/omit-from-array';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import ManageDrivers from '../../components/manage-drivers';
import { IManageDriversProps } from '../../components/manage-drivers/types';
import { useIsMounted } from '@acorn-portal/shared/src/hooks/useIsMounted';
import handleManageDrivers from '../../utils/handle-manage-drivers';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const MTAManageDrivers = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const isMounted = useIsMounted();
	const { track } = useTrack();

	useEffect(() => {
		track(`Manage Drivers Page Viewed`);
	}, []);

	const onDiscard = () => navigate(-1);
	const mtaState = useAppSelector((redux) => redux.midTermAdjustment);
	const vehicleUse = mtaState.vehicleDetails.vehicleUse;
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const activePolicyValues = activePolicyDetails.drivers;
	const [hasChanged] = useNoChangeWarning();

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_DRIVER,
		setAdjustmentLoading: setLoading,
	});

	const {
		additionalDrivers,
		setAdditionalDrivers,
		getAdditionalDriversMta,
		onRemoveClick,
		onEditClick,
		showForm,
		onSetMainDriver,
		toggleForm,
		setSelectedItem,
		setItem,
		onFullFormSubmit,
		onShortFormSubmit,
		isDriverModalOpen,
		toggleDriverModal,
		onDelete,
		selectedItem,
	} = useManageAdditionalDriverDetails({
		midTermAdjustment,
	});

	async function submitMtaChanges() {
		if (!additionalDrivers) return null;

		const updatedAdditionalDrivers = additionalDrivers.map((driver) =>
			omitFromArray(driver, ['otherEmployment', 'isNewDriver']),
		);

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, updatedAdditionalDrivers),
			hasChanged(mtaState.drivers, additionalDrivers),
			mtaResponse,
			mtaState.changeOfDriver,
			location.state?.drivers,
		);

		if (!continueToRate && !navigateToSummary) {
			//Initial MTA - No Changes
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}
		setLoading(true);

		try {
			const result = await handleManageDrivers({
				midTermAdjustment: {
					...midTermAdjustment,
					drivers: location.state?.drivers || midTermAdjustment.drivers,
					changeOfDriver: location.state?.drivers ? true : false,
				},
				values: additionalDrivers,
				product: fullPolicy.product,
			});

			if (!result) throw new Error('No result from handleManageDrivers');
			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}
			dispatch(
				updateMidTermAdjustmentDetails({
					...midTermAdjustment,
					drivers: additionalDrivers,
					changeOfDriver: updateSection,
				}),
			);
			dispatch(setMidTermAdjustmentRatingResponse(result));
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		} catch (error) {
			console.error('Error submitting MTA changes:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	}
	const onSubmitMta = () => {
		submitMtaChanges();
	};

	useEffect(() => {
		if (additionalDrivers === undefined || additionalDrivers.length === 0) {
			getAdditionalDriversMta();
		}

		if (!mtaState.policyReference && isMounted) {
			navigate(ROUTE_PATHS.home);
		}
	}, [mtaState, getAdditionalDriversMta]);

	const props: IManageDriversProps = {
		loading,
		additionalDrivers,
		setAdditionalDrivers,
		vehicleUse,
		onRemoveClick,
		onEditClick,
		showForm,
		onSetMainDriver,
		toggleForm,
		setItem,
		setSelectedItem,
		onFullFormSubmit,
		onShortFormSubmit,
		openDriverModal: isDriverModalOpen,
		toggleDriverModal,
		onDelete,
		onDiscard,
		onPageSubmit: onSubmitMta,
		selectedItem,
	};

	return <ManageDrivers {...props} />;
};

export default MTAManageDrivers;
